// DRIVE IN REAL TIME (Karan 07/10/2026: "if something is added to drive, it
// needs to be shown in realtime"). While a Drive screen is open AND the tab is
// visible, Drive's changes feed is read every 30 seconds — one cheap call — and
// the folders on screen that it touches are handed to the screen to re-read.
// Nothing runs when the tab is hidden or the screen has been left: the router
// gives each route its own host, so `mount.isConnected` is the lifetime.
//
// The page token is the person's, kept in memory and localStorage, so a return
// visit reads "what changed since I last looked" rather than starting cold.

import { driveStartPageToken, driveChanges } from './google-api.js';
import { foldersTouched } from './live-refresh.js';

const KEY = (who) => `track-drive-changes:${String(who || 'anon').toLowerCase()}`;
const mem = new Map();
const getTok = (who) => { if (mem.has(who)) return mem.get(who); try { return localStorage.getItem(KEY(who)); } catch (e) { return null; } };
const setTok = (who, t) => { mem.set(who, t); try { localStorage.setItem(KEY(who), t); } catch (e) { /* memory still works */ } };

/**
 * startDriveLive({ mount, who, watched: () => ids, known: () => {fileId→parents},
 *   onTouched: async (folderIds) => {} }) → stop().
 * `watched` is read each tick, so a screen that loads more folders is covered.
 */
export function startDriveLive({ mount, who, watched, known = () => ({}), onTouched, everyMs = 30000 }) {
  let busy = false;
  let timer = null;
  let stopped = false;
  const stop = () => { stopped = true; clearInterval(timer); document.removeEventListener('visibilitychange', onVis); };
  async function tick() {
    if (stopped) return;
    if (!mount.isConnected) { stop(); return; }
    if (document.hidden || busy) return;
    busy = true;
    try {
      let tok = getTok(who);
      if (!tok) { setTok(who, await driveStartPageToken()); return; }   // first visit: start from now
      const { changes, next } = await driveChanges(tok);
      if (next) setTok(who, next);
      if (!changes.length || stopped || !mount.isConnected) return;
      const hit = foldersTouched(changes, watched() || [], known() || {});
      if (hit.length) await onTouched(hit);
    } catch (e) {
      // An expired token (Drive answers 410/400) starts again from now.
      if (/410|invalid|page token/i.test(String(e && e.message))) { try { setTok(who, await driveStartPageToken()); } catch (_) { /* next tick */ } }
    } finally { busy = false; }
  }
  const onVis = () => { if (!document.hidden) tick(); };   // back on the tab: catch up at once
  document.addEventListener('visibilitychange', onVis);
  timer = setInterval(tick, everyMs);
  tick();
  return stop;
}
